import { Check, Clear } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { debounce, isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getMostSevereStatus,
  getStatusColor,
  getLastMeasureResults,
} from "../constants";
import FormatMeasureValue from "./FormatMeasureValue";
import HubSearchForm from "./HubSearchForm";
import { usePagination } from "components/hooks";
import MeasureList from "components/MeasureList";
import VirtualHubIndicator from "components/VirtualHubIndicator";
import { toQueryString } from "utils";
import { get } from "utils/io";

export default function HubList({
  programId,
  measureCharts,
  isCKD,
  deviceTypes,
  aggregateValues,
  isDynamicProgram,
}) {
  const theme = useTheme();
  const history = useHistory();
  const [autoRefreshMeasure, setAutoRefreshMeasure] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [searchParams, setSearchParams] = useState({
    hubId: undefined,
    status: undefined,
  });

  const { paginationModel, setPaginationModel, totalCount, setTotalCount } =
    usePagination();

  const [hubs, setHubs] = useState([]);

  const dataGridColumns = [
    {
      field: "hub_id",
      flex: 0.5,
      headerName: "Hub ID",
      renderCell: (params) => {
        const { lastMeasures } = params.row;
        const results = getLastMeasureResults(lastMeasures || {});
        const allAlerts = results.reduce(
          (acc, curr) => ({ ...acc, ...curr.measure_alert }),
          {}
        );
        const mostSevereStatus = getMostSevereStatus(allAlerts);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: getStatusColor(
                  mostSevereStatus?.value?.status || "normal"
                )?.color,
              }}
            >
              {params.row.hubId}
            </Typography>
            {params.row.isVirtual && (
              <Box sx={{ ml: "10px" }}>
                <VirtualHubIndicator />
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "connected",
      align: "center",
      flex: 1,
      headerAlign: "center",
      headerName: "Connected",
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          {params.row.connected ? (
            <Check sx={{ color: theme.palette.success.dark }} />
          ) : (
            <Clear color="error" />
          )}
        </Box>
      ),
      width: 150,
    },
    {
      field: "measureValue",
      flex: 1,
      headerName: "Measure Value",
      sortable: false,
      renderCell: (params) => {
        const { lastMeasures } = params.row;
        const results = getLastMeasureResults(lastMeasures);
        if (!results.length) return "N/A";

        const pulseox = results.find((item) => item.type === "pulseox");
        const bloodpressure = results.find(
          (item) => item.type === "bloodpressure"
        );

        if (pulseox && bloodpressure) {
          const olderEntry =
            new Date(pulseox.timestamp) < new Date(bloodpressure.timestamp)
              ? pulseox
              : bloodpressure;
          delete olderEntry.value.bpm;
          delete olderEntry.measure_alert?.bpm;
        }

        return (
          <Box sx={{ display: "flex", gap: "3px", alignItems: "center" }}>
            {results.map((result, index) => (
              <React.Fragment key={result.type}>
                {index > 0 && <span>|</span>}
                <FormatMeasureValue
                  key={result.type}
                  type={result.type}
                  value={result.value}
                  measureAlert={result.measure_alert}
                />
              </React.Fragment>
            ))}
          </Box>
        );
      },
      width: 180,
    },
    {
      field: "last_measure",
      flex: 1,
      headerName: "Last measure",
      renderCell: (params) => {
        const { lastMeasures } = params.row;
        if (isEmpty(lastMeasures)) return "_";

        const latesTime = Object.values(lastMeasures).reduce((a, b) =>
          new Date(a.timestamp) > new Date(b.timestamp) ? a : b
        );

        return new Date(latesTime.timestamp).toLocaleString();
      },
      width: 200,
    },
  ];

  const handleSearch = (params) => {
    setSearchParams(params);
  };

  const fetchHubs = useCallback(
    async (sortField) => {
      setLoading(true);

      let shouldIncludeAggregateValues = false;
      if (isDynamicProgram) {
        shouldIncludeAggregateValues =
          aggregateValues !== null && aggregateValues !== undefined;
      } else {
        shouldIncludeAggregateValues = aggregateValues === true;
      }

      const payload = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        measure_types: isDynamicProgram
          ? deviceTypes
          : measureCharts.map(({ type }) => type),
        ...(searchParams.hubId && { hub_id: searchParams.hubId }),
        ...(searchParams.status && { alert_status: searchParams.status }),
        ...(sortField && { ordering: sortField }),
      };

      if (shouldIncludeAggregateValues) {
        payload.include_aggregate_values = true;
      }

      const queryString = toQueryString(payload);

      const res = await get(`/programs/${programId}/hubs?${queryString}`);
      const hubs = res?.data?.hubs || [];
      const totalCount = res?.data?.total_count || 0;

      const mappedHubs = hubs.map(
        ({
          connected,
          hub_group: hubGroup,
          version_status: versionStatus,
          is_virtual: isVirtual,
          hub_id: hubId,
          last_measures: lastMeasures,
        }) => ({
          id: hubId,
          hubId,
          connected,
          hubGroup,
          versionStatus,
          isVirtual,
          lastMeasures,
          averageBP: null, // This will be populated by future API calls
        })
      );
      setHubs(mappedHubs);
      setTotalCount(totalCount);
      setLoading(false);
    },
    [
      programId,
      paginationModel,
      measureCharts,
      setTotalCount,
      searchParams,
      aggregateValues,
      deviceTypes,
      isDynamicProgram,
    ]
  );

  const updateHubGrid = useCallback(() => {
    const debouncedUpdate = debounce(async () => {
      setLoading(true);
      const sortField = sortModel[0]?.field;
      const sortDirection = sortModel[0]?.sort === "desc" ? "-" : "";
      const sortParam = sortField ? `${sortDirection}${sortField}` : undefined;

      await fetchHubs(sortParam);
      setLoading(false);
    }, 300);

    debouncedUpdate();
    return debouncedUpdate.cancel;
  }, [fetchHubs, sortModel, setLoading]);

  useEffect(() => {
    if (autoRefreshMeasure) {
      const interval = setInterval(updateHubGrid, 10000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefreshMeasure, updateHubGrid]);

  useEffect(() => {
    updateHubGrid();
  }, [updateHubGrid]);

  return (
    <Stack spacing={3}>
      <HubSearchForm onSubmit={handleSearch} isCKD={isCKD} />
      <MeasureList
        loading={loading}
        columnsConfig={dataGridColumns}
        rows={hubs}
        onRowClick={({ row }) =>
          history.push(`/programs/${programId}?hub=${row.hubId}`)
        }
        sortingMode="server"
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        rowsPerPageOptions={[20, 25, 50, 100]}
        rowCount={totalCount}
        setAutoRefreshMeasure={setAutoRefreshMeasure}
        autoRefreshMeasure={autoRefreshMeasure}
        showDeliveries={false}
        setSortModel={setSortModel}
        toolbarProps={{
          visibleToolbarItems: { autoRefresh: true },
        }}
      />
    </Stack>
  );
}

HubList.propTypes = {
  programId: PropTypes.string.isRequired,
  measureCharts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          color: PropTypes.string,
          bounds: PropTypes.arrayOf(
            PropTypes.oneOfType([
              PropTypes.number,
              PropTypes.shape({
                color: PropTypes.string,
                value: PropTypes.number,
              }),
            ])
          ),
        })
      ),
    })
  ).isRequired,
  deviceTypes: PropTypes.arrayOf(PropTypes.string),
  isDynamicProgram: PropTypes.bool,
  isCKD: PropTypes.bool,
  aggregateValues: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
};

HubList.defaultProps = {
  deviceTypes: [],
  isDynamicProgram: false,
  isCKD: false,
  aggregateValues: null,
};
